import React, { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Copy, CreditCard, Key, Loader2, Info, Send, Webhook, TestTube, CheckCircle } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useMpesaTransactions } from "@/hooks/useMpesaTransactions";
import { MpesaTransactionsTable } from "./MpesaTransactionsTable";

export const MpesaTab = () => {
  const { toast } = useToast();
  const { transactions, isLoading: transactionsLoading, subscribeToTransactions, testCallback } = useMpesaTransactions();
  
  // Payment request states
  const [consumerKey, setConsumerKey] = useState("VrlyVCqb2xC3rUQNAySa6rYADN29LalPvcnulm7bXCQcJOvz");
  const [consumerSecret, setConsumerSecret] = useState("yz5H3WD3tf2KsDeRW1Rqmma7B9ZyNnkdOCQzo7UzVGSBZT8qDAAFJ1aQDHumvPyz");
  const [basicAuth, setBasicAuth] = useState("");
  const [accessToken, setAccessToken] = useState("");
  const [expiresIn, setExpiresIn] = useState("");
  const [isRequestingToken, setIsRequestingToken] = useState(false);
  
  // Payment request states
  const [businessShortCode, setBusinessShortCode] = useState("174379");
  const [password, setPassword] = useState("MTc0Mzc5YmZiMjc5ZjlhYTliZGJjZjE1OGU5N2RkNzFhNDY3Y2QyZTBjODkzMDU5YjEwZjc4ZTZiNzJhZGExZWQyYzkxOTIwMjUwNTI1MjAyMTU0");
  const [timestamp, setTimestamp] = useState("20250525202154");
  const [transactionType, setTransactionType] = useState("CustomerPayBillOnline");
  const [amount, setAmount] = useState("1");
  const [partyA, setPartyA] = useState("254700230965");
  const [partyB, setPartyB] = useState("174379");
  const [phoneNumber, setPhoneNumber] = useState("254700230965");
  const [callBackURL, setCallBackURL] = useState("https://mydomain.com/path");
  const [accountReference, setAccountReference] = useState("LamuLamu");
  const [transactionDesc, setTransactionDesc] = useState("Payment of Vendor X");
  const [isRequestingPayment, setIsRequestingPayment] = useState(false);
  const [paymentResponse, setPaymentResponse] = useState("");

  // Callback testing states
  const [testCheckoutRequestId, setTestCheckoutRequestId] = useState("");
  const [testMerchantRequestId, setTestMerchantRequestId] = useState("");
  const [testResultCode, setTestResultCode] = useState(0);

  // Subscribe to real-time transaction updates
  useEffect(() => {
    const unsubscribe = subscribeToTransactions();
    return unsubscribe;
  }, [subscribeToTransactions]);

  // Extract checkout request ID from payment response for testing
  useEffect(() => {
    if (paymentResponse) {
      try {
        const response = JSON.parse(paymentResponse);
        if (response.data?.CheckoutRequestID) {
          setTestCheckoutRequestId(response.data.CheckoutRequestID);
        }
        if (response.data?.MerchantRequestID) {
          setTestMerchantRequestId(response.data.MerchantRequestID);
        }
      } catch (e) {
        // Ignore parsing errors
      }
    }
  }, [paymentResponse]);

  // Get the callback URL
  const getCallbackUrl = () => {
    return `https://ginefbvgxcobyuocxerb.supabase.co/functions/v1/mpesa-callback`;
  };

  const handleTestCallback = async (resultCode: number) => {
    if (!testCheckoutRequestId || !testMerchantRequestId) {
      toast({
        title: "Error",
        description: "Please request a payment first to get test IDs",
        variant: "destructive",
      });
      return;
    }

    testCallback.mutate({
      checkoutRequestId: testCheckoutRequestId,
      merchantRequestId: testMerchantRequestId,
      resultCode: resultCode,
      amount: parseInt(amount),
      phoneNumber: phoneNumber
    });
  };

  const handleEncode = () => {
    try {
      if (!consumerKey.trim() || !consumerSecret.trim()) {
        toast({
          title: "Error",
          description: "Both Consumer Key and Consumer Secret are required",
          variant: "destructive",
        });
        return;
      }

      const credentials = `${consumerKey}:${consumerSecret}`;
      const encoded = btoa(credentials);
      const basicAuthString = `Basic ${encoded}`;
      
      setBasicAuth(basicAuthString);
      
      toast({
        title: "Success",
        description: "Credentials encoded successfully",
        variant: "success",
      });
    } catch (error) {
      console.error('Error encoding credentials:', error);
      toast({
        title: "Error",
        description: "Failed to encode credentials",
        variant: "destructive",
      });
    }
  };

  const handleRequestToken = async () => {
    if (!consumerKey.trim() || !consumerSecret.trim()) {
      toast({
        title: "Error",
        description: "Please provide both Consumer Key and Consumer Secret",
        variant: "destructive",
      });
      return;
    }

    setIsRequestingToken(true);
    
    try {
      console.log('Requesting MPesa token via edge function');
      
      const { data, error } = await supabase.functions.invoke('mpesa-token', {
        body: {
          consumerKey: consumerKey.trim(),
          consumerSecret: consumerSecret.trim()
        }
      });
      
      if (error) {
        console.error('Edge function error:', error);
        throw new Error(error.message || 'Failed to call MPesa service');
      }
      
      if (!data.success) {
        console.error('MPesa API error:', data.error);
        throw new Error(data.error || 'MPesa API returned an error');
      }
      
      console.log('MPesa token received successfully');
      
      setAccessToken(data.access_token);
      setExpiresIn(data.expires_in);
      setBasicAuth(data.basic_auth);
      
      toast({
        title: "Success",
        description: "Access token generated successfully",
        variant: "success",
      });
      
    } catch (error) {
      console.error('Error requesting token:', error);
      toast({
        title: "Error",
        description: `Failed to request token: ${error.message}`,
        variant: "destructive",
      });
    } finally {
      setIsRequestingToken(false);
    }
  };

  const handleRequestPayment = async () => {
    if (!accessToken) {
      toast({
        title: "Error",
        description: "Please request an access token first",
        variant: "destructive",
      });
      return;
    }

    setIsRequestingPayment(true);
    setPaymentResponse("");

    try {
      console.log('Requesting MPesa payment via edge function...');
      
      const { data, error } = await supabase.functions.invoke('mpesa-payment', {
        body: {
          accessToken: accessToken,
          businessShortCode: parseInt(businessShortCode),
          password: password,
          timestamp: timestamp,
          transactionType: transactionType,
          amount: parseInt(amount),
          partyA: parseInt(partyA),
          partyB: parseInt(partyB),
          phoneNumber: parseInt(phoneNumber),
          callBackURL: getCallbackUrl(), // Use our callback URL
          accountReference: accountReference,
          transactionDesc: transactionDesc
        }
      });
      
      if (error) {
        console.error('Edge function error:', error);
        throw new Error(error.message || 'Failed to call MPesa payment service');
      }
      
      console.log('MPesa payment response received:', data);
      
      setPaymentResponse(JSON.stringify(data, null, 2));
      
      if (data.success) {
        toast({
          title: "Success",
          description: "Payment request sent successfully",
          variant: "success",
        });
      } else {
        toast({
          title: "Payment Request",
          description: `Request completed with status: ${data.status}`,
          variant: data.success ? "success" : "destructive",
        });
      }
      
    } catch (error) {
      console.error('Error requesting payment:', error);
      const errorMessage = `Failed to request payment: ${error.message}`;
      setPaymentResponse(errorMessage);
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setIsRequestingPayment(false);
    }
  };

  const copyToClipboard = async (text: string, label: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast({
        title: "Copied",
        description: `${label} copied to clipboard`,
        variant: "success",
      });
    } catch (error) {
      console.error('Failed to copy:', error);
      toast({
        title: "Error",
        description: "Failed to copy to clipboard",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="space-y-6">
      {/* Step 1: Encode Credentials */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CreditCard className="h-5 w-5" />
            MPesa Sandbox Integration Testing
          </CardTitle>
          <CardDescription>
            Step 1 of 4: Encode Consumer Key and Secret for MPesa Sandbox authentication.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Step 1 content */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="consumerKey">Consumer Key</Label>
              <div className="flex gap-2">
                <Input
                  id="consumerKey"
                  value={consumerKey}
                  onChange={(e) => setConsumerKey(e.target.value)}
                  placeholder="Enter Consumer Key"
                />
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => copyToClipboard(consumerKey, "Consumer Key")}
                  disabled={!consumerKey}
                >
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="consumerSecret">Consumer Secret</Label>
              <div className="flex gap-2">
                <Input
                  id="consumerSecret"
                  value={consumerSecret}
                  onChange={(e) => setConsumerSecret(e.target.value)}
                  placeholder="Enter Consumer Secret"
                  type="password"
                />
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => copyToClipboard(consumerSecret, "Consumer Secret")}
                  disabled={!consumerSecret}
                >
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>

          <div className="flex justify-center pt-4">
            <Button onClick={handleEncode} className="px-8">
              Encode
            </Button>
          </div>

          {basicAuth && (
            <div className="space-y-2">
              <Label htmlFor="basicAuth">Basic Authorization</Label>
              <div className="flex gap-2">
                <Textarea
                  id="basicAuth"
                  value={basicAuth}
                  readOnly
                  className="min-h-[80px] bg-gray-50"
                />
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => copyToClipboard(basicAuth, "Basic Authorization")}
                  className="mt-0"
                >
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Step 2: Request Access Token */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Key className="h-5 w-5" />
            Step 2: Request Access Token
          </CardTitle>
          <CardDescription>
            Use our backend service to securely request an access token from MPesa Sandbox.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Step 2 content */}
          <div className="flex justify-center">
            <Button 
              onClick={handleRequestToken} 
              disabled={!consumerKey || !consumerSecret || isRequestingToken}
              className="px-8"
            >
              {isRequestingToken ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Requesting Token...
                </>
              ) : (
                "Request Token"
              )}
            </Button>
          </div>

          {accessToken && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="accessToken">Access Token</Label>
                <div className="flex gap-2">
                  <Input
                    id="accessToken"
                    value={accessToken}
                    readOnly
                    className="bg-gray-50 font-mono text-sm"
                  />
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => copyToClipboard(accessToken, "Access Token")}
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="expiresIn">Expires In (seconds)</Label>
                <div className="flex gap-2">
                  <Input
                    id="expiresIn"
                    value={expiresIn}
                    readOnly
                    className="bg-gray-50"
                  />
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => copyToClipboard(expiresIn, "Expires In")}
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              <div className="p-3 bg-green-50 border border-green-200 rounded-md">
                <p className="text-sm text-green-700">
                  ✅ Access token generated successfully! This token is valid for {expiresIn} seconds.
                </p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Step 3: Request Payment - abbreviated */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Send className="h-5 w-5" />
            Step 3: Request Payment (STK Push)
          </CardTitle>
          <CardDescription>
            The callback URL has been automatically set to: <code className="bg-gray-100 px-1 rounded">{getCallbackUrl()}</code>
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Step 3 payment form */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="amount">Amount</Label>
              <Input
                id="amount"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="1"
                type="number"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="phoneNumber">Phone Number</Label>
              <Input
                id="phoneNumber"
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
                placeholder="254700230965"
              />
            </div>
          </div>

          <div className="flex justify-center pt-4">
            <Button 
              onClick={handleRequestPayment} 
              disabled={!accessToken || isRequestingPayment}
              className="px-8"
            >
              {isRequestingPayment ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Requesting Payment...
                </>
              ) : (
                "Request Payment"
              )}
            </Button>
          </div>

          {paymentResponse && (
            <div className="space-y-2">
              <Label htmlFor="paymentResponse">Payment Response</Label>
              <Textarea
                id="paymentResponse"
                value={paymentResponse}
                readOnly
                className="min-h-[200px] bg-gray-50 font-mono text-sm"
              />
            </div>
          )}
        </CardContent>
      </Card>

      {/* NEW: Step 4: Handle Callbacks */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Webhook className="h-5 w-5" />
            Step 4: Handle Payment Callbacks
          </CardTitle>
          <CardDescription>
            Monitor payment confirmations and test callback functionality.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Callback URL Info */}
          <div className="space-y-2">
            <Label htmlFor="callbackUrl">MPesa Callback URL</Label>
            <div className="flex gap-2">
              <Input
                id="callbackUrl"
                value={getCallbackUrl()}
                readOnly
                className="bg-gray-50 font-mono text-sm"
              />
              <Button
                variant="outline"
                size="icon"
                onClick={() => copyToClipboard(getCallbackUrl(), "Callback URL")}
              >
                <Copy className="h-4 w-4" />
              </Button>
            </div>
            <p className="text-sm text-gray-600">
              Configure this URL in your MPesa merchant portal as the callback URL for payment confirmations.
            </p>
          </div>

          {/* Test Callback Buttons */}
          {testCheckoutRequestId && (
            <div className="space-y-4">
              <div className="p-4 bg-blue-50 border border-blue-200 rounded-md">
                <h4 className="font-medium text-blue-900 mb-2">Test Callback Simulation</h4>
                <p className="text-sm text-blue-700 mb-3">
                  Test different payment scenarios using the request IDs from your last payment request.
                </p>
                <div className="flex gap-2 flex-wrap">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleTestCallback(0)}
                    disabled={testCallback.isPending}
                    className="text-green-600 border-green-300 hover:bg-green-50"
                  >
                    <CheckCircle className="h-4 w-4 mr-1" />
                    Test Success
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleTestCallback(1)}
                    disabled={testCallback.isPending}
                    className="text-red-600 border-red-300 hover:bg-red-50"
                  >
                    <TestTube className="h-4 w-4 mr-1" />
                    Test Failure
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleTestCallback(1032)}
                    disabled={testCallback.isPending}
                    className="text-orange-600 border-orange-300 hover:bg-orange-50"
                  >
                    <TestTube className="h-4 w-4 mr-1" />
                    Test Cancelled
                  </Button>
                </div>
                {testCallback.isPending && (
                  <p className="text-sm text-blue-600 mt-2 flex items-center">
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Sending test callback...
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Transactions Table */}
          <MpesaTransactionsTable 
            transactions={transactions} 
            isLoading={transactionsLoading} 
          />
        </CardContent>
      </Card>

      {/* Updated Final Steps */}
      <Card>
        <CardHeader>
          <CardTitle>Integration Status & Next Steps</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="space-y-2 text-sm text-gray-600">
              <p><strong>Step 1:</strong> ✅ Encode Consumer Key and Secret</p>
              <p><strong>Step 2:</strong> {accessToken ? "✅" : "⏳"} Generate Access Token</p>
              <p><strong>Step 3:</strong> {paymentResponse ? "✅" : "⏳"} Request Payment via STK Push</p>
              <p><strong>Step 4:</strong> ✅ Handle payment callbacks and confirmations</p>
            </div>
            
            <Alert variant="info">
              <Info className="h-4 w-4" />
              <AlertTitle>Production Configuration</AlertTitle>
              <AlertDescription>
                <div className="mt-2 space-y-2">
                  <p><strong>For production deployment:</strong></p>
                  <ul className="list-disc list-inside space-y-1 text-sm">
                    <li>Configure the callback URL: <code className="bg-gray-100 px-1 rounded">{getCallbackUrl()}</code></li>
                    <li>Switch to production MPesa endpoints</li>
                    <li>Update credentials to production keys</li>
                    <li>Implement proper error handling and retry logic</li>
                    <li>Set up monitoring and alerts for failed transactions</li>
                  </ul>
                </div>
              </AlertDescription>
            </Alert>

            {transactions.length > 0 && (
              <div className="p-3 bg-green-50 border border-green-200 rounded-md">
                <p className="text-sm text-green-700">
                  <strong>✅ Callback System Active:</strong> {transactions.length} transaction(s) received via callbacks.
                  The system is ready to handle real MPesa payments.
                </p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
